import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { usePermissions } from "../../../PermissionsProvider";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { BarChart3Icon } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TrainingMetricEntry {
  loss?: number;
  eval_loss?: number;
  learning_rate?: number;
  epoch?: number;
  step?: number;
  grad_norm?: number;
  // eval entries have different keys
  eval_runtime?: number;
  eval_samples_per_second?: number;
  eval_steps_per_second?: number;
}

interface TrainingMetricsChartProps {
  finetuneRunId: number;
}

const TrainingMetricsChart: React.FC<TrainingMetricsChartProps> = ({
  finetuneRunId,
}) => {
  const { apiKey } = usePermissions();
  const [metrics, setMetrics] = useState<TrainingMetricEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!finetuneRunId) return;

    const sub = FinetuneRunService.getTrainingMetrics(
      finetuneRunId,
      setLoading,
      apiKey
    ).subscribe(
      (data: TrainingMetricEntry[]) => {
        setMetrics(data || []);
      },
      (error: any) => {
        console.error("Failed to load training metrics:", error);
        setMetrics([]);
        setLoading(false);
      }
    );
    return () => sub.unsubscribe();
  }, [finetuneRunId, apiKey]);

  // Separate train and eval metric entries
  const trainEntries = useMemo(
    () => metrics.filter((e) => e.loss !== undefined && e.step !== undefined),
    [metrics]
  );

  const evalEntries = useMemo(
    () => metrics.filter((e) => e.eval_loss !== undefined),
    [metrics]
  );

  const hasEval = evalEntries.length > 0;

  const emaSmooth = (values: number[], alpha = 0.98): (number | null)[] => {
    if (values.length === 0) return [];
    let ema = values[0];
    return values.map((v, i) => {
      if (v === null || v === undefined) return null;
      ema = i === 0 ? v : alpha * ema + (1 - alpha) * v;
      return ema;
    });
  };

  const lossChartData = useMemo(() => {
    if (trainEntries.length === 0) return null;

    const labels = trainEntries.map((e) => String(e.step));
    const stepToIndex = new Map<number, number>();
    trainEntries.forEach((e, i) => stepToIndex.set(e.step!, i));

    const rawData = trainEntries.map((e) => e.loss!);
    const smoothedData = emaSmooth(rawData);

    const datasets: any[] = [
      {
        label: "Train Loss (raw)",
        data: rawData,
        borderColor: "rgba(59, 130, 246, 0.15)",
        backgroundColor: "rgba(59, 130, 246, 0.05)",
        borderWidth: 1,
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: "#3b82f6",
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 1,
        order: 2,
        legend: false,
      },
      {
        label: "Train Loss",
        data: smoothedData,
        borderColor: "#3b82f6",
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: "#3b82f6",
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 2,
        order: 1,
      },
    ];

    if (hasEval) {
      const evalData: (number | null)[] = new Array(labels.length).fill(null);

      const evalWithStep = evalEntries.filter((e) => e.step !== undefined);
      if (evalWithStep.length > 0) {
        for (const e of evalWithStep) {
          const idx = stepToIndex.get(e.step!);
          if (idx !== undefined) {
            evalData[idx] = e.eval_loss!;
          } else {
            let closestIdx = 0;
            let closestDist = Infinity;
            stepToIndex.forEach((i, step) => {
              const dist = Math.abs(step - e.step!);
              if (dist < closestDist) {
                closestDist = dist;
                closestIdx = i;
              }
            });
            evalData[closestIdx] = e.eval_loss!;
          }
        }
      } else {
        for (const e of evalEntries) {
          let bestIdx = 0;
          let bestDist = Infinity;
          for (let i = 0; i < trainEntries.length; i++) {
            const dist = Math.abs(
              (trainEntries[i].epoch || 0) - (e.epoch || 0)
            );
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = i;
            }
          }
          evalData[bestIdx] = e.eval_loss!;
        }
      }

      datasets.push({
        label: "Eval Loss",
        data: evalData,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.1)",
        borderWidth: 2.5,
        fill: false,
        tension: 0.3,
        spanGaps: true,
        pointRadius: evalData.map((v) => (v !== null ? 5 : 0)),
        pointBackgroundColor: "#f59e0b",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        order: 0,
      });
    }

    return { labels, datasets };
  }, [trainEntries, evalEntries, hasEval]);

  // Build the learning rate chart data
  const lrChartData = useMemo(() => {
    const lrEntries = trainEntries.filter(
      (e) => e.learning_rate !== undefined
    );
    if (lrEntries.length === 0) return null;

    return {
      labels: lrEntries.map((e) => String(e.step)),
      datasets: [
        {
          label: "Learning Rate",
          data: lrEntries.map((e) => e.learning_rate!),
          borderColor: "#21b892",
          backgroundColor: "rgba(33, 184, 146, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [trainEntries]);

  const lossOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: "index" as const },
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: "#737373",
            font: { size: 12 },
            usePointStyle: true,
            pointStyle: "line",
            filter: (item: any) => item.text !== "Train Loss (raw)",
          },
        },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#ffffff",
          bodyColor: "#ffffff",
          borderColor: "#3b82f6",
          borderWidth: 1,
          cornerRadius: 6,
          filter: (item: any) => item.parsed.y !== null,
          callbacks: {
            title: (context: any) => `Step ${context[0].label}`,
            label: (context: any) => {
              const val = context.parsed.y;
              if (val === null || val === undefined) return "";
              return `${context.dataset.label}: ${val.toFixed(4)}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: "Training Step", color: "#737373" },
          grid: { display: false },
          ticks: {
            color: "#737373",
            font: { size: 11 },
            maxTicksLimit: 10,
          },
        },
        y: {
          title: { display: true, text: "Loss", color: "#737373" },
          grid: { color: "rgba(115, 115, 115, 0.1)" },
          ticks: { color: "#737373", font: { size: 11 } },
        },
      },
    }),
    []
  );

  const lrOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: "index" as const },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#ffffff",
          bodyColor: "#ffffff",
          borderColor: "#21b892",
          borderWidth: 1,
          cornerRadius: 6,
          callbacks: {
            title: (context: any) => `Step ${context[0].label}`,
            label: (context: any) =>
              `LR: ${context.parsed.y.toExponential(2)}`,
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: "Training Step", color: "#737373" },
          grid: { display: false },
          ticks: {
            color: "#737373",
            font: { size: 11 },
            maxTicksLimit: 10,
          },
        },
        y: {
          title: {
            display: true,
            text: "Learning Rate",
            color: "#737373",
          },
          grid: { color: "rgba(115, 115, 115, 0.1)" },
          ticks: { color: "#737373", font: { size: 11 } },
        },
      },
    }),
    []
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BarChart3Icon className="w-5 h-5 text-maitai-green" />
              Training Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center justify-center">
              <div className="space-y-3 w-full">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!metrics || metrics.length === 0 || !lossChartData) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BarChart3Icon className="w-5 h-5 text-maitai-green" />
              Training Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex items-center justify-center">
              <div className="text-center space-y-3">
                <h3 className="text-lg font-gc-med text-foreground">
                  No training metrics available
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Training metrics will appear here once a training run
                  completes. Metrics are recorded during training and include
                  loss curves and learning rate schedules.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Summary stats
  const finalTrainLoss = trainEntries[trainEntries.length - 1]?.loss;
  const finalEvalLoss =
    evalEntries.length > 0
      ? evalEntries[evalEntries.length - 1]?.eval_loss
      : null;
  const totalSteps = trainEntries[trainEntries.length - 1]?.step || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground font-gc-reg">
              Final Train Loss
            </p>
            <p className="text-2xl font-gc-med text-foreground">
              {finalTrainLoss?.toFixed(4) || "-"}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground font-gc-reg">
              Final Eval Loss
            </p>
            <p className="text-2xl font-gc-med text-foreground">
              {finalEvalLoss?.toFixed(4) || "-"}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground font-gc-reg">
              Total Steps
            </p>
            <p className="text-2xl font-gc-med text-foreground">
              {totalSteps}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Loss Chart */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <BarChart3Icon className="w-5 h-5 text-maitai-green" />
            Loss
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <Line data={lossChartData} options={lossOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Learning Rate Chart */}
      {lrChartData && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BarChart3Icon className="w-5 h-5 text-maitai-green" />
              Learning Rate Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <Line data={lrChartData} options={lrOptions} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TrainingMetricsChart;
